// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: userProfiles/$walletAddress
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Persistent user profiles for Danger Dan! community. Stores a display name that persists across Danger Room messages and other features. Document ID is the user's wallet address for easy lookups.
 */

export interface UserProfilesRequest {
  displayName: string;
  updatedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface UserProfilesResponse {
  displayName: string;
  updatedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a UserProfiles operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUserProfiles(walletAddress: string, data: UserProfilesRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `userProfiles/${walletAddress}`, document: doc };
}

/**
 * Authenticated users only. The document ID ($walletAddress) must match the caller's wallet address, ensuring users can only create their own profile. displayName is required. updatedAt is a Unix seconds timestamp for tracking last update. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUserProfiles(walletAddress: string, data: UserProfilesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`userProfiles/${walletAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting UserProfiles: ${formatError(error)}`);
    return false;
  }
}

export type UserProfilesRequestUpdate = Partial<UserProfilesRequest>;

/**
 * Build a UserProfiles update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateUserProfiles(walletAddress: string, data: UserProfilesRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `userProfiles/${walletAddress}`, document: doc };
}

/**
 * Only the profile owner can update. The caller's wallet must match the document ID ($walletAddress). Used to change displayName and updatedAt timestamp. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateUserProfiles(walletAddress: string, data: UserProfilesRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`userProfiles/${walletAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating UserProfiles: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read user profiles. Used to display persistent usernames across Danger Room chat and other community features.
   (Get Single Item)
 */
export async function getUserProfiles(walletAddress: string): Promise<UserProfilesResponse | null> {
  try {
    const result = await get(`userProfiles/${walletAddress}`);
    return result as UserProfilesResponse | null;
  } catch (error) {
    console.error(`Error getting UserProfiles: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single UserProfiles document. (
  Read Operation Details: Anyone can read user profiles. Used to display persistent usernames across Danger Room chat and other community features.
  )
 */
export function subscribeUserProfiles(callback: (data: UserProfilesResponse | null) => void, walletAddress: string): Promise<() => Promise<void>> {
  return subscribe(`userProfiles/${walletAddress}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UserProfilesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to UserProfiles at path '${`userProfiles/${walletAddress}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many UserProfiles items from collection userProfiles
 
  Read Operation Details: Anyone can read user profiles. Used to display persistent usernames across Danger Room chat and other community features.
  
 */
export async function getManyUserProfiles(filter: string = ""): Promise<UserProfilesResponse[]> {
  try {
    const results = await get("userProfiles", { prompt: filter });
    return Array.isArray(results) ? results as UserProfilesResponse[] : [];
  } catch (error) {
    console.error(`Error getting UserProfiles collection at path 'userProfiles': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UserProfiles collection at userProfiles
 
  Read Operation Details: Anyone can read user profiles. Used to display persistent usernames across Danger Room chat and other community features.
  
 */
export function subscribeManyUserProfiles(callback: (data: UserProfilesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("userProfiles", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UserProfilesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UserProfiles collection at path '${"userProfiles"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all UserProfiles items from collection userProfiles
 
  Read Operation Details: Anyone can read user profiles. Used to display persistent usernames across Danger Room chat and other community features.
  
 */
export async function getAllUserProfiles(filter: string = ""): Promise<UserProfilesResponse[]> {
  try {
    const results = await get("userProfiles", { prompt: filter });
    return Array.isArray(results) ? results as UserProfilesResponse[] : [];
  } catch (error) {
    console.error(`Error getting UserProfiles collection at path 'userProfiles': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UserProfiles collection at userProfiles
 
  Read Operation Details: Anyone can read user profiles. Used to display persistent usernames across Danger Room chat and other community features.
  
 */
export function subscribeAllUserProfiles(callback: (data: UserProfilesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("userProfiles", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UserProfilesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UserProfiles collection at path '${"userProfiles"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count UserProfiles items in collection userProfiles.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countUserProfiles(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("userProfiles", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting UserProfiles at path 'userProfiles': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on UserProfiles items in collection userProfiles.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateUserProfiles(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("userProfiles", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating UserProfiles at path 'userProfiles': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin or founder only. Can delete profiles for moderation purposes (e.g., removing offensive usernames).
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteUserProfiles(walletAddress: string): Promise<boolean> {
  try {
    const succeeded = await set(`userProfiles/${walletAddress}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting UserProfiles: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for UserProfiles for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteUserProfiles(walletAddress: string): DocumentOperation {
  return { path: `userProfiles/${walletAddress}`, document: null };
}

